import type { DocArticle } from './documents.ts';
import { GRANTING_EXCHANGE_ACCESS, GRANTING_PURVIEW_ACCESS } from './accessContent.ts';

/**
 * Getting a client into the product: what the platform is, connecting a
 * tenant, and what the tenant's own licences decide.
 */

const WHAT_THIS_IS: DocArticle = {
  id: 'what-this-is',
  title: 'What DriftWatch does',
  summary: 'The shape of the product, and the promises it makes about its own answers.',
  sections: [
    {
      id: 'the-job',
      heading: 'The job',
      paragraphs: [
        'DriftWatch reads a client’s Microsoft 365 and Entra configuration, read-only, evaluates it against a versioned catalogue of eighty-two controls, and produces a report an auditor can use. It reads; it never writes. Nothing in the product changes anything in a client’s tenant.',
        'Each control is a deterministic rule over collected configuration. The same configuration produces the same verdict, and every verdict carries the evidence and the rule version that produced it.',
      ],
    },
    {
      id: 'four-verdicts',
      heading: 'Four verdicts, and no score',
      paragraphs: [
        'A control is Met, Not met, Not applicable, or Not evidenced. Not applicable means the thing the control governs does not exist in this tenant. Not evidenced means we could not find out — and each one says why.',
        'There is no percentage anywhere in the product, and there will not be. A score invites a client to manage the number rather than the risk, and it hides which control moved. The report counts, and it names.',
      ],
    },
    {
      id: 'what-it-is-not',
      heading: 'What it is not',
      paragraphs: [
        'It is not an audit and it is not a certification. The report says so on its own first page. A framework projection shows which criteria the assessed controls speak to, never that a criterion is satisfied in full — most criteria are satisfied by evidence that lives outside a Microsoft 365 tenant.',
        'Where a verdict and its written explanation disagree, the verdict is correct and the explanation is a defect. Please report it.',
      ],
    },
    {
      id: 'honest-gaps',
      heading: 'A gap is reported as a gap',
      paragraphs: [
        'Three things can stop a control being decided: the client has not bought the licence that exposes the configuration, our application was not permitted to read it, or no Microsoft API exposes it at all. All three are reported as open questions, never as failures, and never counted towards a failure total.',
      ],
    },
  ],
};

const CONNECTING_A_CLIENT: DocArticle = {
  id: 'connecting-a-client',
  title: 'Connecting a client',
  summary: 'Adding a client, sending the consent link, and what each connection state means.',
  sections: [
    {
      id: 'add',
      heading: 'Add the client',
      paragraphs: [
        'Clients → Add a client. You need the client’s name and their Microsoft tenant id — the directory (tenant) id from their Entra admin centre. The id is validated on the server, which will say if it is not a tenant id.',
        'If the deployment has no Entra application registered, the form is replaced by a notice saying so. That is a deployment setting, not something you can fix on this screen.',
      ],
    },
    {
      id: 'consent',
      heading: 'Send the consent link',
      paragraphs: [
        'Creating a client produces a consent link. Copy it and send it to the client’s Global Administrator yourself: DriftWatch never emails your clients. You can re-open the link at any time from the client’s screen.',
        'Opening that link is how the client grants DriftWatch its read-only consent. It must be approved by a Global Administrator or a Privileged Role Administrator; no other role can grant tenant-wide consent.',
        'If a Microsoft tenant is already connected to another account, adding it is refused. Moving a client between accounts is a transfer, and we do it rather than a form.',
      ],
    },
    {
      id: 'states',
      heading: 'What the connection states mean',
      paragraphs: [
        'A client’s state is written by what actually happened, not by a checkbox. Consent alone does not make a client Connected — the first successful assessment does.',
      ],
      bullets: [
        'Connected — at least one configuration read succeeded.',
        'Awaiting consent — the link has not been approved, or the administrator closed the tab.',
        'Consent revoked — consent existed and has since been withdrawn.',
        'Insufficient permissions — every read was refused. Usually consent granted against an older permission set; send a fresh link.',
        'Connection error — the last attempt failed for another reason, recorded against the client.',
      ],
    },
    {
      id: 'exchange',
      heading: 'Exchange needs one more step',
      paragraphs: [
        'Exchange Online ignores the Graph consent and requires its own role assignment, made by the client in their tenant through PowerShell. Until it exists, the Exchange and email controls report as open questions rather than failures.',
        'Three view-only roles are needed: View-Only Configuration, View-Only Recipients and View-Only Audit Logs. DriftWatch checks for them on every run and reads nothing from Exchange until they are present. Do not grant Exchange Administrator or Global Reader instead: the first can write, so DriftWatch refuses to use it, and the second is not an Exchange role assignment.',
        'The exact commands the client runs, in order, are in the next article, Granting Exchange access.',
      ],
    },
    {
      id: 'purview',
      heading: 'Purview needs a step of its own',
      paragraphs: [
        'Microsoft Purview accepts neither the Graph consent nor the Exchange roles. DriftWatch has to be added, through PowerShell, to a role group holding three view-only roles: View-Only DLP Compliance Management, Sensitivity Label Reader and View-Only Retention Management. Until it is, DP-024, DP-025 and DP-026 report as open questions rather than failures, and nothing else is affected.',
        'The exact commands are in the article after Exchange’s, Granting Purview access.',
      ],
    },
    {
      id: 'archiving',
      heading: 'Archiving and purging',
      paragraphs: [
        'Archiving is reversible. A client leaves every live list — the estate, the sweep, the consent chase — and nothing they have recorded is removed. You can unarchive them at any time.',
        'Purging is not reversible, and a client must be archived first. It asks you to type the client’s exact name before the button will do anything. Both actions need the deployment capability, which only an owner holds.',
      ],
    },
  ],
};

const LICENCES: DocArticle = {
  id: 'licences',
  title: 'What a client’s licences decide',
  summary: 'The four capabilities that change what can be assessed, and what each one costs when missing.',
  sections: [
    {
      id: 'four',
      heading: 'Four capabilities',
      paragraphs: [
        'Most of the catalogue reads configuration available on any Microsoft 365 plan. Four capabilities are the exception, and DriftWatch checks for each before it asks a question that depends on it.',
        'A capability counts as held when the tenant has purchased seats for it and the service plan is provisioned — not when a particular person has been assigned one.',
      ],
      bullets: [
        'Microsoft Entra ID P1 — Conditional Access, sign-in activity.',
        'Microsoft Entra ID P2 — Privileged Identity Management, Identity Protection.',
        'Microsoft Intune — device compliance, configuration, enrolment, app protection.',
        'Microsoft Purview Information Protection — sensitivity labels.',
      ],
    },
    {
      id: 'licence-effect',
      heading: 'What a missing licence does',
      paragraphs: [
        'Thirteen controls depend on a licence. Without Entra ID P1, the Conditional Access control and the device-compliance-for-access control report as Not applicable: the client has not bought the capability, so there is nothing to assess. Without Entra ID P2, Intune or Purview, the affected controls report as Not evidenced with the licence named.',
        'Two further controls behave the same way because of what they read rather than what they declare: the dormant-accounts control needs sign-in activity, and the break-glass control reads the Conditional Access policies those accounts must be excluded from. Both are Entra ID P1 features.',
      ],
    },
    {
      id: 'undetectable',
      heading: 'Licences we cannot check in advance',
      paragraphs: [
        'Exchange Online plans, Defender for Office 365 and the wider Purview suite have no advance check. A gap there appears when the configuration is read: the command is simply absent from the session, and the affected controls report a licence gap at that point. The answer is correct either way; it is only discovered later.',
      ],
    },
    {
      id: 'sspr',
      heading: 'One control no licence can answer',
      paragraphs: [
        'Self-service password reset is reported as Not evidenced on every tenant at every licence tier, because Microsoft Graph does not expose that configuration at all. It must be checked by hand in the Entra admin centre, under Protection → Password reset.',
      ],
    },
  ],
};

export const CONNECTING: readonly DocArticle[] = [
  WHAT_THIS_IS,
  CONNECTING_A_CLIENT,
  GRANTING_EXCHANGE_ACCESS,
  GRANTING_PURVIEW_ACCESS,
  LICENCES,
];
